'use client';

import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  Dim,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  Typography,
} from '@musinsa/mds';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { OptionSheetFooter } from './OptionSheetFooter';
import { RejectReasonCheckboxRow } from './RejectReasonCheckboxRow';

interface ProposeRejectReasonSheetProps {
  open: boolean;
  onClose: () => void;
  /** 거절 사유 목록 (code/label). 시딩 상세·제안 목록 공용 — code 는 API 문자열. */
  reasons: { code: string; label: string }[];
  onSubmit: (rejectionReasonCodes: string[]) => void;
  /** submit 진행 중 disabled — 중복 호출 방지 */
  submitting?: boolean;
  /** 진입 시 prefill 할 사유 code 들 (mock/검수 용) */
  initialReasons?: string[];
  className?: string;
}

const SHEET_TITLE = '거절하는 이유를 선택해주세요';

export const ProposeRejectReasonSheet = ({
  open,
  onClose,
  reasons,
  onSubmit,
  submitting,
  initialReasons,
  className,
}: ProposeRejectReasonSheetProps) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initialReasons ?? []));

  useEffect(() => {
    if (!open) setSelected(new Set(initialReasons ?? []));
  }, [open, initialReasons]);

  const toggle = (code: string, next: boolean) => {
    setSelected((prev) => {
      const draft = new Set(prev);
      if (next) draft.add(code);
      else draft.delete(code);
      return draft;
    });
  };

  const handleSubmit = () => {
    if (selected.size === 0 || submitting) return;
    onSubmit(Array.from(selected));
  };

  return (
    <Sheet direction="bottom" open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetPortal>
        <SheetContent onPointerDownOutside={onClose}>
          <SheetMotion direction="bottom">
            <div
              className={twMerge(
                'flex max-h-[584px] flex-col bg-white rounded-t-[8px] safe-area-bottom',
                className,
              )}
            >
              {/* 핸들·타이틀 — 고정 헤더 */}
              <div className="shrink-0">
                <BottomSheetHandleArea />
                <BottomSheetTitle>
                  <div className="flex items-center justify-between px-[16px]! py-[9px]!">
                    <Typography variant="textEtc16Med" colorScheme="black">
                      {SHEET_TITLE}
                    </Typography>
                    <IconButton size="size28x28" onClick={onClose} aria-label="닫기">
                      <IcDelete />
                    </IconButton>
                  </div>
                </BottomSheetTitle>
              </div>
              {/* 사유 목록 — 많을 때 내부 스크롤. min-h-0 로 max-h 안에서 overflow 동작. */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-[4px]! pb-[24px]! px-[16px]! gap-y-[24px]!">
                {reasons.map((reason) => (
                  <RejectReasonCheckboxRow
                    key={reason.code}
                    value={reason.code}
                    label={reason.label}
                    checked={selected.has(reason.code)}
                    onChange={(next) => toggle(reason.code, next)}
                  />
                ))}
              </div>
              {/* 푸터 — 고정 */}
              <div className="shrink-0">
                <OptionSheetFooter
                  variant="dual-cta"
                  secondary={{ label: '취소', onClick: onClose }}
                  primary={{
                    label: '거절',
                    onClick: handleSubmit,
                    disabled: selected.size === 0 || submitting,
                    loading: submitting,
                  }}
                />
              </div>
            </div>
          </SheetMotion>
        </SheetContent>
        <SheetDimMotion>
          <Dim />
        </SheetDimMotion>
      </SheetPortal>
    </Sheet>
  );
};
